import { ARTICLE_TOKENS } from '@components/article/design-tokens';
import Paywall from '@components/paywall';
import { ClubSection } from '@components/predicted-lineups/ClubSection';
import type { PublishedLineup } from '@api-service/predicted-lineups';
import type { PremiumTeamSectionsState } from '@hooks/usePremiumTeamSections';
import { EMPTY_ARTICLE_BLOCK_DATA } from '@lib/article-blocks/hydrate';
import { findLineupForClub } from '@lib/predicted-lineups/compose';
import type {
  ClubDescriptor,
  ClubHeading
} from '@lib/predicted-lineups/compose';
import type { PlayerFactsMap } from '@lib/predicted-lineups/player-facts';

const PAYWALL_TITLE = 'Team news for the remaining clubs is for members';
const PAYWALL_SUBTITLE =
  'Pro and Ultra members read every club’s notes, injuries, doubts and suspensions.';

const LoadingWords = () => (
  <div
    className="my-6 space-y-3"
    data-cy="club-words-loading"
    aria-busy="true"
    aria-label="Loading team news"
  >
    {['w-full', 'w-11/12', 'w-3/4'].map((width, index) => (
      <div
        key={index}
        className={`h-4 animate-pulse rounded bg-neutral-200 ${width}`}
      />
    ))}
  </div>
);

const LockedWords = () => (
  <p
    className={`my-6 rounded-[14px] border px-5 py-4 text-center text-sm ${ARTICLE_TOKENS.rule} ${ARTICLE_TOKENS.surfaceTint} ${ARTICLE_TOKENS.bodyMuted}`}
    data-cy="club-words-locked"
  >
    Team news, injuries and doubts for this club are for Pro and Ultra members.
  </p>
);

const LoadFailed = ({ retry }: { retry: () => void }) => (
  <div
    className={`my-6 rounded-[14px] border p-5 text-center ${ARTICLE_TOKENS.rule} ${ARTICLE_TOKENS.surfaceTint}`}
    data-cy="club-words-error"
    role="alert"
  >
    <p className={`m-0 mb-3 text-sm ${ARTICLE_TOKENS.body}`}>
      We could not load the team news.
    </p>
    <button
      type="button"
      onClick={retry}
      className="rounded-lg border-2 border-transparent bg-primary-600 px-3 py-[7px] font-open-sans text-base font-bold text-white transition-colors hover:bg-primary-800"
    >
      Try again
    </button>
  </div>
);

type GatedClubsProps = {
  clubs: ClubDescriptor[];
  headings: ClubHeading[];
  lineups: PublishedLineup[];
  playerFacts: PlayerFactsMap;
  state: PremiumTeamSectionsState;
  returnTo: string;
};

/**
 * The clubs without a free section. Their headings and pitches are public and
 * render for everyone; the words in each come from the premium read, so a
 * visitor sees the paywall once, then a locked note per club, until they are
 * entitled and the words arrive.
 */
export const GatedClubs = ({
  clubs,
  headings,
  lineups,
  playerFacts,
  state,
  returnTo
}: GatedClubsProps) => {
  if (clubs.length === 0) return null;

  const wordsFor = (teamShortName: string) =>
    state.status === 'ready'
      ? state.sections.find(
          (section) => section.teamShortName === teamShortName
        )
      : undefined;

  const fallback =
    state.status === 'loading' ? (
      <LoadingWords />
    ) : state.status === 'error' ? (
      <LoadFailed retry={state.retry} />
    ) : (
      <LockedWords />
    );

  return (
    <div data-cy="gated-clubs">
      {state.status === 'locked' && (
        <div className="mt-14" data-cy="club-paywall">
          <Paywall
            variant="embedded"
            title={PAYWALL_TITLE}
            subtitle={PAYWALL_SUBTITLE}
            returnTo={returnTo}
          />
        </div>
      )}
      {clubs.map((club, index) => (
        <ClubSection
          key={headings[index]?.id ?? club.teamShortName}
          club={club}
          heading={
            headings[index] ?? {
              id: club.teamShortName,
              text: club.teamShortName,
              teamShortName: club.teamShortName
            }
          }
          lineup={findLineupForClub(lineups, club.teamShortName)}
          playerFacts={playerFacts}
          words={wordsFor(club.teamShortName)}
          blockData={
            state.status === 'ready'
              ? state.blockData
              : EMPTY_ARTICLE_BLOCK_DATA
          }
          wordsFallback={fallback}
        />
      ))}
    </div>
  );
};
